import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AcademyHeader from '../../../components/LP/AcademyHeader';
import LPFooter from '../../../components/LP/LPFooter';
import SEO from '../../../components/SEO/SEO';

const purposes = [
  { id: 'academy_apply', title: 'アカデミーへの申し込み', defaultMessage: 'SrWithアカデミーに申し込みたいです。' },
  { id: 'academy_detail', title: 'アカデミーの詳細を聞きたい', defaultMessage: 'SrWithアカデミーについて詳しく教えてください。' },
  { id: 'consultation', title: '個別相談の予約', defaultMessage: '個別相談を希望します。' },
  { id: 'other', title: 'その他のご質問', defaultMessage: '' },
];

const AcademyContactPage = () => {
  const [searchParams] = useSearchParams();
  const initialPurpose = searchParams.get('purpose') || '';
  const initialMessage = purposes.find((p) => p.id === initialPurpose)?.defaultMessage || '';
  const [selectedPurpose, setSelectedPurpose] = useState(initialPurpose);
  const [formData, setFormData] = useState(initialMessage ? { inquiry: initialMessage } : {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleTab, setActiveScheduleTab] = useState(0);

  useEffect(() => {
    fetch('/api/admin-read.php?file=academy-schedules&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setSchedules)
      .catch(() => setSchedules([]));
  }, []);

  // 1ヶ月目の最初の日付イベントが過ぎていたら受付終了
  const isScheduleClosed = (schedule) => {
    if (schedule.closed) return true;
    const firstMonth = schedule.months?.[0];
    if (!firstMonth) return false;
    const firstEvent = firstMonth.events?.find((ev) => ev.date && ev.date !== '不定期配信');
    if (!firstEvent) return false;
    // YYYY-MM-DD をローカル時間で解釈
    const parts = firstEvent.date.split('-');
    if (parts.length !== 3) return false;
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    if (isNaN(d)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d <= today;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePurposeSelect = (id) => {
    setSelectedPurpose(id);
    const purpose = purposes.find((p) => p.id === id);
    if (purpose?.defaultMessage) {
      setFormData((prev) => ({ ...prev, inquiry: purpose.defaultMessage }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userType: 'sr',
          purposeType: selectedPurpose,
          ...formData,
        }),
      });
      const data = await response.json();
      setSubmitResult(data.success ? 'success' : 'error');
    } catch {
      setSubmitResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-noto bg-lp-cream text-lp-charcoal">
      <SEO
        title="個別相談・お問い合わせ｜SrWithアカデミー"
        description="SrWithアカデミーへのお申し込み、個別相談のご予約、その他ご質問はこちらから。"
      />

      <AcademyHeader />

      <main className="flex-grow pt-[48px]">
        <section className="relative bg-lp-charcoal overflow-hidden">
          <div className="relative max-w-[860px] mx-auto px-5 py-16 md:py-20 text-center">
            <h1 className="font-noto-serif text-2xl md:text-4xl font-bold text-white mb-4">
              個別相談・お問い合わせ
            </h1>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-[540px] mx-auto mb-8">
              お気軽にご相談ください。押し売りは一切ありません。
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <a
                href="https://timerex.net/s/shotsu923_b21b/84385a85"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 transition-opacity text-sm md:text-base"
              >
                個別相談を予約する（無料）
              </a>
              <button
                onClick={() => setShowSchedule(true)}
                className="inline-block px-8 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:border-white transition-all text-sm md:text-base"
              >
                スケジュールを見る
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[600px] mx-auto px-5">

            {submitResult === 'success' ? (
              <div className="bg-white rounded-2xl p-8 md:p-12 text-center shadow-lg">
                <div className="text-4xl mb-4">&#10003;</div>
                <h2 className="font-noto-serif text-xl md:text-2xl font-bold text-lp-charcoal mb-4">
                  お問い合わせありがとうございます
                </h2>
                <p className="text-sm text-lp-charcoal-mid leading-relaxed mb-8">
                  内容を確認の上、1〜2営業日以内にご連絡いたします。
                </p>
                <a
                  href="/lp/academy"
                  className="inline-block px-8 py-3 bg-lp-rose text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
                >
                  アカデミーページに戻る
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 shadow-lg space-y-6">

                {/* ハニーポット */}
                <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                  <input
                    type="text"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    onChange={(e) => handleInputChange('website_url', e.target.value)}
                  />
                </div>

                {/* 目的選択 */}
                <div>
                  <label className="block text-sm font-bold text-lp-charcoal mb-3">ご用件 *</label>
                  <div className="space-y-2">
                    {purposes.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handlePurposeSelect(p.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                          selectedPurpose === p.id
                            ? 'border-lp-rose bg-lp-rose/5 text-lp-charcoal font-bold'
                            : 'border-gray-200 text-lp-charcoal-mid hover:border-gray-300'
                        }`}
                      >
                        {p.title}
                      </button>
                    ))}
                  </div>
                </div>

                {/* お名前 */}
                <div>
                  <label className="block text-sm font-bold text-lp-charcoal mb-2">お名前 *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                    placeholder="例: 田中太郎"
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>

                {/* メールアドレス */}
                <div>
                  <label className="block text-sm font-bold text-lp-charcoal mb-2">メールアドレス *</label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                    placeholder="例: info@example.com"
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                {/* 事務所名 */}
                <div>
                  <label className="block text-sm font-bold text-lp-charcoal mb-2">事務所名（任意）</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                    onChange={(e) => handleInputChange('officeName', e.target.value)}
                  />
                </div>

                {/* お問い合わせ内容 */}
                <div>
                  <label className="block text-sm font-bold text-lp-charcoal mb-2">お問い合わせ内容</label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm resize-none"
                    value={formData.inquiry || ''}
                    onChange={(e) => handleInputChange('inquiry', e.target.value)}
                  />
                </div>

                {submitResult === 'error' && (
                  <p className="text-red-600 text-sm text-center">送信に失敗しました。時間をおいて再度お試しください。</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !selectedPurpose}
                  className="w-full py-4 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* スケジュールモーダル */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSchedule(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#9b7a82] px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">2026年 アカデミースケジュール</h2>
              <button onClick={() => setShowSchedule(false)} className="text-white hover:text-gray-200 text-2xl font-light">×</button>
            </div>
            <div className="p-6">
              {/* 凡例 */}
              <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
                <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded font-bold">勉強会 1回目</span>
                <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-bold">勉強会 2回目</span>
                <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold">勉強会 3回目</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-bold">実践スキルアップLAB</span>
                <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold">動画コンテンツ</span>
              </div>

              {/* タブ */}
              {schedules.length > 0 && (
                <>
                  <div className="flex gap-1 mb-0">
                    {schedules.map((s, i) => (
                      <button key={s.id} onClick={() => setActiveScheduleTab(i)}
                        className={`px-4 py-2 rounded-t-lg text-sm font-bold ${activeScheduleTab === i ? 'bg-[#9b7a82] text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'} ${isScheduleClosed(s) ? 'opacity-60' : ''}`}>
                        {s.startMonth}{isScheduleClosed(s) ? '（受付終了）' : ''}
                      </button>
                    ))}
                  </div>

                  {/* テーブル */}
                  <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 w-24">受講月</th>
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 w-16">月</th>
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">イベント</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {schedules[activeScheduleTab]?.months.map((month, mi) => {
                          const hasStudy = month.events.some((ev) => ev.type.startsWith('study'));
                          return (
                            <tr key={mi} className={hasStudy ? 'bg-pink-50' : ''}>
                              <td className="px-4 py-3 text-xs font-bold text-[#9b7a82]">{month.monthNum}ヶ月目</td>
                              <td className="px-4 py-3 text-xs font-bold text-gray-700">{month.calMonth}</td>
                              <td className="px-4 py-3">
                                {month.events.map((ev, ei) => {
                                  const colors = {
                                    study1: 'bg-yellow-100 text-yellow-800',
                                    study2: 'bg-orange-100 text-orange-800',
                                    study3: 'bg-red-100 text-red-800',
                                    lab: 'bg-purple-100 text-purple-800',
                                    video: 'bg-green-100 text-green-800',
                                  };
                                  const labels = {
                                    study1: 'アカデミー勉強会 1回目',
                                    study2: 'アカデミー勉強会 2回目',
                                    study3: 'アカデミー勉強会 3回目',
                                    lab: '実践スキルアップLAB',
                                    video: '動画コンテンツ',
                                  };
                                  const formatDate = (d) => {
                                    if (!d || d === '不定期配信') return d;
                                    if (d.includes('（') || d.includes('/')) return d;
                                    const dt = new Date(d);
                                    if (isNaN(dt)) return d;
                                    const days = ['日','月','火','水','木','金','土'];
                                    return `${dt.getMonth()+1}/${dt.getDate()}（${days[dt.getDay()]}）`;
                                  };
                                  return (
                                    <div key={ei} className="mb-1 last:mb-0">
                                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded mr-2 ${colors[ev.type] || 'bg-gray-100'}`}>{labels[ev.type] || ev.type}</span>
                                      <span className="text-xs text-gray-600">{formatDate(ev.date)}{ev.time ? ` ${ev.time}` : ''}</span>
                                    </div>
                                  );
                                })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-[11px] text-gray-500 space-y-1">
                    <p>📌 アカデミー勉強会は6ヶ月間で計3回（1・3・6ヶ月目）開催されます。</p>
                    <p>📌 実践スキルアップLABは毎月開催。アカデミー生全員参加可。</p>
                    <p>📌 勉強会・LABはアーカイブ動画でもご覧いただけます。</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <LPFooter />
    </div>
  );
};

export default AcademyContactPage;
